"use client";

import React from "react";
import { motion } from "framer-motion";
import { ShieldCheck, Flame, Droplets, CircleCheck as CheckCircle2, SlidersHorizontal as Sliders, Award, FileText, Gauge, ArrowRight, Sparkles } from 'lucide-react';
import { Link } from "@/components/common/Link";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
const technicalAdvantages = [{
  id: "tech-1",
  title: "Atomização ultra-precisa",
  description: "Gotículas milimetricamente calibradas para máxima absorção térmica com menor consumo de água.",
  icon: Droplets,
  metric: "-40% água"
}, {
  id: "tech-2",
  title: "Ativação e resposta imediata",
  description: "Bicos concebidos para distribuição uniforme sem atrasos na formação do padrão de cobertura.",
  icon: Flame,
  metric: "< 1.5s resposta"
}, {
  id: "tech-3",
  title: "Resistência a meios corrosivos",
  description: "Ligas de aço inoxidável 316L, titânio e Hastelloy para ambientes marítimos, químicos e pesados.",
  icon: ShieldCheck,
  metric: "100% testado"
}, {
  id: "tech-4",
  title: "Controlo de caudal constante",
  description: "Geometrias internas de bico resistentes à obstrução para fiabilidade total em emergência.",
  icon: Gauge,
  metric: "Zero obstrução"
}];
const standardsList = [{
  id: "std-1",
  code: "NFPA 13, 15 & 750",
  name: "Normas Globais de Segurança",
  description: "Conformidade rigorosa para sistemas de pulverização de água fixa e sistemas de névoa de água (Water Mist)."
}, {
  id: "std-2",
  code: "EN 12845 & EN 14972",
  name: "Normativa Europeia",
  description: "Dimensionamento e desempenho certificados para instalações industriais automáticas de proteção contra incêndio."
}, {
  id: "std-3",
  code: "Certificação ATEX",
  name: "Ambientes com Risco de Explosão",
  description: "Componentes e bicos aptos para zonas classificadas na indústria química, petroquímica e farmacêutica."
}, {
  id: "std-4",
  code: "ISO 9001 & FM Approved",
  name: "Qualidade e Fiabilidade",
  description: "Processos de fabrico rastreáveis da Spraying Systems Co. com aprovações internacionais independentes."
}];
const solutionsData = [{
  id: "sol-1",
  tabKey: "water-mist",
  tabLabel: "Névoa de Água (Water Mist)",
  title: "Sistemas de névoa de água de alta eficiência",
  description: "Projetados para supressão rápida através de microgotas que evaporam instantaneamente, arrefecendo a fonte e sufocando as chamas sem causar danos estruturais ou inundação excessiva.",
  featuresText: "Ideal para turbinas, salas elétricas, túneis e centros de dados; Redução no consumo de água até 80%; Rápido retorno à operação após incidente.",
  badgeText: "Alta Pressão"
}, {
  id: "sol-2",
  tabKey: "deluge",
  tabLabel: "Sistemas de Dilúvio",
  title: "Proteção de grande área por dilúvio e cortina",
  description: "Descarga volumétrica simultânea para áreas críticas de processamento de combustíveis, refinarias e tanques de armazenamento químico, contendo a propagação térmica em segundos.",
  featuresText: "Bicos de ângulo aberto com ampla densidade de fluxo; Proteção perimetral e resfriamento contínuo de tanques; Lanças e manifolds customizados.",
  badgeText: "Industrial Pesado"
}, {
  id: "sol-3",
  tabKey: "curtains",
  tabLabel: "Cortinas de Proteção",
  title: "Barreiras térmicas e contenção de gases tóxicos",
  description: "Bicos lineares de impacto que criam uma parede de fluido denso para dissipar calor radiante e bloquear a dispersão acidental de fumos e vapores voláteis.",
  featuresText: "Atenuação de radiação térmica até 90%; Proteção de rotas de evacuação e fachadas; Compatível com agentes espumíferos.",
  badgeText: "Segurança de Pessoal"
}];
const faqs = [{
  id: "faq-1",
  question: "Qual a diferença de rendimento entre os bicos de dilúvio convencionais e a tecnologia Water Mist?",
  answer: "A tecnologia Water Mist utiliza gotas microscópicas que aumentam a área de superfície de contacto com o calor em centenas de vezes. Isto permite absorver o calor muito mais rapidamente e extinguir o fogo com um volume de água até 80% inferior face a sistemas tradicionais."
}, {
  id: "faq-2",
  question: "Os bicos e manifolds cumprem as exigências dos reguladores em Portugal?",
  answer: "Sim. Todos os equipamentos fornecidos pela Eurocontrol através da Spraying Systems Co. possuem certificações europeias (EN) e aprovações internacionais (FM/UL/ATEX) aceites pela ANPC e seguradoras industriais."
}, {
  id: "faq-3",
  question: "É possível personalizar materiais para ambientes com agentes químicos agressivos?",
  answer: "Sim. Produzimos bicos e sistemas em ligas especiais como AISI 316L, Titânio, Hastelloy, PVDF ou Latão Naval, assegurando total resistência química e durabilidade mecânica a longo prazo."
}];
export default function FireProtection() {
  return <section id="fireprotectionsystemsoverview" data-nav="light" className="bg-background text-foreground py-20 lg:py-28 overflow-hidden">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-7xl">
        
        {/* Section Header */}
        <div className="max-w-3xl mb-16 lg:mb-20">
          <motion.div initial={{
          opacity: 0,
          y: 16
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.4,
          ease: "easeOut"
        }} className="flex items-center gap-2 mb-4">
            <Badge variant="outline" className="border-secondary/40 text-secondary bg-secondary/5 font-medium px-3 py-1">
              <ShieldCheck className="size-3.5 mr-1" />
              Sistemas de Proteção Industrial
            </Badge>
          </motion.div>

          <motion.h2 initial={{
          opacity: 0,
          y: 16
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.5,
          delay: 0.08,
          ease: "easeOut"
        }} className="text-3xl sm:text-4xl lg:text-5xl font-default font-bold text-foreground tracking-tight leading-tight">
            Proteção ativa contra incêndios com tecnologia de pulverização
          </motion.h2>

          <motion.p initial={{
          opacity: 0,
          y: 16
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.5,
          delay: 0.16,
          ease: "easeOut"
        }} className="mt-4 text-lg text-muted-foreground leading-relaxed">Na Spraying Systems Co., compreendemos que, em cenários críticos de proteção contra incêndios, não há margem para compromissos. É por isso que a nossa linha completa de bicos de proteção contra incêndios é projetada para proporcionar uma supressão de incêndios fiável e de elevado desempenho numa vasta gama de aplicações e indústrias. Desde sistemas de dilúvio em instalações industriais até sistemas de névoa de água e de supressão química em ambientes especializados, as nossas soluções são concebidas para responder às exigências mais rigorosas.</motion.p>
        </div>

        {/* Technical Advantages Grid */}
        <div className="mb-20">
          <div className="flex items-center justify-between mb-8 pb-3 border-b border-border">
            <div>
              <h3 className="text-xl font-default font-semibold text-foreground">
                Vantagens técnicas fundamentais
              </h3>
              <p className="text-sm text-muted-foreground mt-0.5">
                Desempenho projetado para responder a situações de emergência sem falhas.
              </p>
            </div>
            <div className="hidden md:flex items-center gap-2 text-xs font-mono text-muted-foreground">
              <Sliders className="size-4 text-secondary" />
              Engenharia Spraying Systems Co.
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {technicalAdvantages.map((item, i) => {
            const IconComp = item.icon;
            return <motion.div key={item.id} data-index={i} initial={{
              opacity: 0,
              y: 20
            }} whileInView={{
              opacity: 1,
              y: 0
            }} viewport={{
              once: true
            }} transition={{
              duration: 0.4,
              delay: i * 0.08,
              ease: "easeOut"
            }}>
                  <Card className="h-full border border-border bg-card shadow-sm hover:border-secondary/50 transition-[border-color,box-shadow] duration-200">
                    <CardHeader className="space-y-3 pb-2">
                      <div className="flex items-center justify-between">
                        <div className="size-11 rounded-lg bg-secondary/10 flex items-center justify-center text-secondary">
                          <IconComp className="size-5" />
                        </div>
                        <span className="text-xs font-mono font-semibold px-2 py-0.5 rounded bg-muted text-foreground">
                          {item.metric}
                        </span>
                      </div>
                      <CardTitle className="text-lg font-default text-card-foreground pt-1">
                        {item.title}
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <CardDescription className="text-sm leading-relaxed text-muted-foreground">
                        {item.description}
                      </CardDescription>
                    </CardContent>
                  </Card>
                </motion.div>;
          })}
          </div>
        </div>

        {/* Tabbed Solutions Showcase */}
        <div className="mb-20 bg-muted/40 rounded-2xl p-6 sm:p-8 lg:p-10 border border-border">
          <div className="max-w-2xl mb-8">
            <h3 className="text-2xl font-default font-bold text-foreground tracking-tight">
              Tipos de sistemas e configurações de descarga
            </h3>
            <p className="text-sm text-muted-foreground mt-1">
              Soluções à medida para supressão, arrefecimento e contenção em ambientes de risco elevado.
            </p>
          </div>

          <Tabs defaultValue="water-mist" className="w-full">
            <TabsList className="bg-card border border-border p-1 mb-8 w-full sm:w-auto flex-wrap justify-start h-auto gap-1">
              {solutionsData.map((sol, i) => <TabsTrigger key={sol.id} data-index={i} value={sol.tabKey} className="px-4 py-2 text-sm font-medium">
                  {sol.tabLabel}
                </TabsTrigger>)}
            </TabsList>

            {solutionsData.map((sol, i) => {
            const features = sol.featuresText.split("; ");
            return <TabsContent key={sol.id} data-index={i} value={sol.tabKey} className="mt-0">
                  <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-center bg-card p-6 sm:p-8 rounded-xl border border-border shadow-xs">
                    <div className="lg:col-span-7 space-y-4">
                      <div className="flex items-center gap-2">
                        <Badge variant="secondary" className="text-xs">
                          {sol.badgeText}
                        </Badge>
                      </div>
                      <h4 className="text-xl sm:text-2xl font-default font-semibold text-foreground">
                        {sol.title}
                      </h4>
                      <p className="text-sm text-muted-foreground leading-relaxed">
                        {sol.description}
                      </p>
                      
                      <div className="pt-2 space-y-2">
                        {features.map((feat, featIdx) => <div key={featIdx} data-index={featIdx} className="flex items-start gap-2.5">
                            <CheckCircle2 className="size-4 text-secondary shrink-0 mt-0.5" />
                            <span className="text-sm text-foreground/90">{feat}</span>
                          </div>)}
                      </div>

                      <div className="pt-4 flex flex-wrap items-center gap-3">
                        <Button asChild size="sm" className="font-medium">
                          <Link to="/TechnicalSolutionsPage">
                            Ver especificações técnicas
                            <ArrowRight className="size-3.5 ml-1" />
                          </Link>
                        </Button>
                        <Button asChild variant="outline" size="sm">
                          <Link to="/CatalogsPage">
                            Consultar catálogos
                          </Link>
                        </Button>
                      </div>
                    </div>

                    <div className="lg:col-span-5 flex flex-col justify-center bg-muted/50 rounded-lg p-6 border border-border/80">
                      <div className="space-y-4">
                        <div className="flex items-center gap-3 pb-3 border-b border-border">
                          <div className="size-10 rounded bg-primary text-primary-foreground flex items-center justify-center">
                            <Sparkles className="size-5" />
                          </div>
                          <div>
                            <p className="text-xs font-mono text-muted-foreground">Distribuidor Exclusivo</p>
                            <p className="text-sm font-semibold text-foreground">Spraying Systems Co. Portugal</p>
                          </div>
                        </div>

                        <div className="space-y-2 text-xs text-muted-foreground">
                          <p className="flex justify-between py-1 border-b border-border/60">
                            <span>Pressão de Operação:</span>
                            <span className="font-mono text-foreground font-medium">3 a 140 bar</span>
                          </p>
                          <p className="flex justify-between py-1 border-b border-border/60">
                            <span>Ângulo de Pulverização:</span>
                            <span className="font-mono text-foreground font-medium">60° a 180°</span>
                          </p>
                          <p className="flex justify-between py-1 border-b border-border/60">
                            <span>Configuração de Conexão:</span>
                            <span className="font-mono text-foreground font-medium">NPT / BSPT / Flange</span>
                          </p>
                          <p className="flex justify-between py-1">
                            <span>Manutenção:</span>
                            <span className="font-mono text-foreground font-medium">Filtros integrados</span>
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                </TabsContent>;
          })}
          </Tabs>
        </div>

        {/* Compliance & Standards Section */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-12 mb-20 items-start">
          <div className="lg:col-span-5 space-y-4">
            <Badge variant="outline" className="border-border text-foreground">
              <Award className="size-3.5 mr-1 text-primary" />
              Conformidade e Certificações
            </Badge>
            <h3 className="text-2xl sm:text-3xl font-default font-bold text-foreground tracking-tight">
              Cumprimento rigoroso das normas internacionais
            </h3>
            <p className="text-sm text-muted-foreground leading-relaxed">
              Todos os componentes de combate a incêndio fornecidos pela Eurocontrol obedecem a diretivas de segurança exigidas por seguradoras e gabinetes de engenharia de todo o mundo.
            </p>

            <div className="pt-2">
              <Card className="border border-border bg-card shadow-xs">
                <CardContent className="p-4 flex items-start gap-3">
                  <FileText className="size-5 text-secondary shrink-0 mt-1" />
                  <div>
                    <h4 className="text-sm font-semibold text-foreground">Apoio em projetos de engenharia</h4>
                    <p className="text-xs text-muted-foreground mt-0.5">
                      Disponibilizamos fichas técnicas completas, curvas de caudal e modelação para cálculo hidráulico de redes.
                    </p>
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>

          <div className="lg:col-span-7 grid grid-cols-1 sm:grid-cols-2 gap-4">
            {standardsList.map((std, i) => <motion.div key={std.id} data-index={i} initial={{
            opacity: 0,
            y: 16
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.35,
            delay: i * 0.08,
            ease: "easeOut"
          }}>
                <Card className="h-full border border-border bg-card p-5 shadow-xs">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-xs font-mono font-bold text-primary px-2 py-0.5 bg-primary/10 rounded">
                      {std.code}
                    </span>
                    <ShieldCheck className="size-4 text-muted-foreground" />
                  </div>
                  <h4 className="text-sm font-semibold text-foreground mt-2">
                    {std.name}
                  </h4>
                  <p className="text-xs text-muted-foreground mt-1.5 leading-relaxed">
                    {std.description}
                  </p>
                </Card>
              </motion.div>)}
          </div>
        </div>

        {/* Technical FAQ & Quick Actions */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-start mb-16">
          <div className="lg:col-span-7">
            <h3 className="text-xl sm:text-2xl font-default font-semibold text-foreground mb-4">
              Perguntas frequentes sobre dimensionamento e normas
            </h3>
            <Accordion type="single" collapsible className="w-full">
              {faqs.map((faq, i) => <AccordionItem key={faq.id} data-index={i} value={faq.id} className="border-border">
                  <AccordionTrigger className="text-base font-default font-medium text-foreground hover:no-underline hover:text-secondary text-left">
                    {faq.question}
                  </AccordionTrigger>
                  <AccordionContent className="text-sm text-muted-foreground leading-relaxed">
                    {faq.answer}
                  </AccordionContent>
                </AccordionItem>)}
            </Accordion>
          </div>

          <div className="lg:col-span-5 bg-card border border-border rounded-xl p-6 sm:p-8 shadow-sm flex flex-col justify-between">
            <div>
              <Badge variant="secondary" className="mb-3">
                Suporte de Especialista
              </Badge>
              <h4 className="text-xl font-default font-bold text-foreground">
                Necessita de suporte para o seu sistema de proteção?
              </h4>
              <p className="text-sm text-muted-foreground mt-2 leading-relaxed">
                A nossa equipa técnica analisa o esquema da sua instalação e recomenda a seleção ideal de bicos, caudais e pressões operacionais.
              </p>
            </div>

            <div className="mt-8 pt-6 border-t border-border space-y-3">
              <Button asChild className="w-full justify-center">
                <Link to="/ContactsPage">
                  Solicitar estudo técnico
                </Link>
              </Button>
              <Button asChild variant="outline" className="w-full justify-center">
                <Link to="/ProductsPage">
                  Explorar catálogo de bicos
                </Link>
              </Button>
            </div>
          </div>
        </div>

      </div>
    </section>;
}